import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { CandyButton } from "./CandyButton";
import { Wordmark } from "./Brand";
import { MegaMenu } from "./MegaMenu";
import { AccountMenu } from "./AccountMenu";
import { PRIMARY_NAV } from "../../config/navigation";
import { BLOG } from "../../content/blog";
import { appLink, marketingLink } from "../../config/origins";

/**
 * OriginLink — renders a relative <Link> or an absolute cross-subdomain <a>
 * from the props returned by appLink()/marketingLink(). Keeps SPA routing on
 * the same origin and only hops subdomains when actually needed.
 */
const OriginLink = ({ link, children, ...rest }) =>
  link.href ? (
    <a href={link.href} {...rest}>
      {children}
    </a>
  ) : (
    <Link to={link.to} {...rest}>
      {children}
    </Link>
  );

const linkCls = ({ isActive }) =>
  `font-display text-sm font-semibold transition-colors duration-200 ${
    isActive ? "text-candy" : "text-ink hover:text-candy"
  }`;

// Latest blog post → Resources featured card (auto-refreshes as content grows).
function resourcesFeatured() {
  const post = BLOG?.[0];
  if (!post) return null;
  return {
    eyebrow: "From the blog",
    title: post.title,
    body: post.excerpt,
    to: `/blog/${post.slug}`,
    cta: { label: "Read the blog", to: "/blog" },
  };
}

// Flatten a menu's columns into a single item list for the mobile drawer.
const flatItems = (menu) => menu.columns.flatMap((c) => c.items);

export const PlayfulNavbar = () => {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSection, setOpenSection] = useState(null);

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const featuredByKey = { resources: resourcesFeatured() };

  const renderMobileLink = (item) => {
    const Icon = item.icon;
    const inner = (
      <>
        {Icon ? <Icon size={15} strokeWidth={2} className="text-candy/70" /> : null}
        {item.label}
      </>
    );
    const cls =
      "flex items-center gap-2.5 py-2 text-sm font-semibold text-ink/70 transition-colors hover:text-candy";
    return item.href ? (
      <a key={item.label} href={item.href} onClick={() => setMobileOpen(false)} className={cls}>
        {inner}
      </a>
    ) : (
      <Link key={item.label} to={item.to} onClick={() => setMobileOpen(false)} className={cls}>
        {inner}
      </Link>
    );
  };

  return (
    <>
      <header
        className="sticky top-0 z-40 border-b-2 border-ink bg-paper/90 backdrop-blur-sm"
        data-testid="editorial-navbar"
      >
        <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-5 md:h-20 md:px-8">
          <OriginLink
            link={marketingLink("/")}
            className="shrink-0 no-underline"
            data-testid="logo"
          >
            <Wordmark size="md" tone="ink" accent="candy" />
          </OriginLink>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-6 lg:flex">
            {PRIMARY_NAV.map((nav, i) => (
              <MegaMenu
                key={nav.key}
                label={nav.label}
                menu={nav.menu}
                featured={featuredByKey[nav.key]}
                align={i >= PRIMARY_NAV.length - 2 ? "right" : "left"}
              />
            ))}
            <NavLink to="/pricing" className={linkCls} data-testid="nav-pricing">
              Pricing
            </NavLink>
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden items-center gap-3 lg:flex">
            {!loading && user ? (
              <>
                <CandyButton
                  size="sm"
                  variant="candy"
                  {...appLink("/dashboard")}
                  data-testid="nav-dashboard"
                >
                  Go to Dashboard →
                </CandyButton>
                <AccountMenu user={user} onLogout={onLogout} />
              </>
            ) : !loading ? (
              <>
                <OriginLink
                  link={appLink("/login")}
                  data-testid="nav-login"
                  className="inline-flex min-h-[40px] items-center whitespace-nowrap px-2 font-display text-[13px] font-bold tracking-tight text-ink transition-colors duration-200 hover:text-candy"
                >
                  Log in
                </OriginLink>
                <CandyButton
                  size="sm"
                  variant="paper"
                  {...marketingLink("/contact")}
                  data-testid="nav-demo"
                >
                  Book a demo
                </CandyButton>
                <CandyButton
                  size="sm"
                  variant="candy"
                  {...appLink("/signup")}
                  data-testid="nav-signup"
                >
                  Start free
                </CandyButton>
              </>
            ) : null}
          </div>

          {/* Mobile hamburger */}
          <button
            className="rounded-xl border-2 border-ink p-2 shadow-sticker-xs transition-colors hover:bg-ink/5 lg:hidden"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle navigation"
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X size={18} strokeWidth={2.5} /> : <Menu size={18} strokeWidth={2.5} />}
          </button>
        </div>
      </header>

      {/* Mobile drawer — sibling of <header> so its fixed positioning is
          viewport-relative; the header's backdrop-blur creates a containing
          block that would otherwise collapse the drawer's height. */}
      {mobileOpen && (
        <div className="fixed inset-x-0 bottom-0 top-16 z-50 flex flex-col overflow-y-auto border-t-2 border-ink bg-paper lg:hidden">
          {/* Quick actions pinned at top for thumb reach */}
          <div className="flex flex-col gap-2 border-b-2 border-ink/10 px-5 py-4">
            {!loading && user ? (
              <>
                <CandyButton
                  size="md"
                  variant="candy"
                  {...appLink("/dashboard")}
                  block
                  onClick={() => setMobileOpen(false)}
                >
                  Go to Dashboard →
                </CandyButton>
                <CandyButton size="sm" variant="paper" onClick={onLogout} block>
                  Sign out
                </CandyButton>
              </>
            ) : (
              <>
                <CandyButton
                  size="md"
                  variant="candy"
                  {...appLink("/signup")}
                  block
                  onClick={() => setMobileOpen(false)}
                >
                  Start free
                </CandyButton>
                <div className="flex gap-2">
                  <CandyButton
                    size="sm"
                    variant="paper"
                    {...appLink("/login")}
                    block
                    onClick={() => setMobileOpen(false)}
                  >
                    Log in
                  </CandyButton>
                  <CandyButton
                    size="sm"
                    variant="paper"
                    {...marketingLink("/contact")}
                    block
                    onClick={() => setMobileOpen(false)}
                  >
                    Book a demo
                  </CandyButton>
                </div>
              </>
            )}
          </div>

          {/* Section accordions */}
          <div className="flex flex-col px-5 py-2">
            {PRIMARY_NAV.map((nav) => (
              <div key={nav.key} className="border-b border-ink/10">
                <button
                  className="flex w-full items-center justify-between py-3 font-display text-base font-semibold text-ink"
                  onClick={() => setOpenSection((s) => (s === nav.key ? null : nav.key))}
                  aria-expanded={openSection === nav.key}
                >
                  {nav.label}
                  <ChevronDown
                    size={16}
                    strokeWidth={2.5}
                    className={`transition-transform duration-200 ${openSection === nav.key ? "rotate-180 text-candy" : "text-ink/60"}`}
                  />
                </button>
                {openSection === nav.key && (
                  <div className="flex flex-col gap-0.5 pb-3 pl-1">
                    {flatItems(nav.menu).map(renderMobileLink)}
                  </div>
                )}
              </div>
            ))}
            <Link
              to="/pricing"
              onClick={() => setMobileOpen(false)}
              className="py-3 font-display text-base font-semibold text-ink"
            >
              Pricing
            </Link>
          </div>
        </div>
      )}
    </>
  );
};

export default PlayfulNavbar;
